<template>
  <div class="recommendation-form">
    <v-simple-table class="recommendation-form__table">
      <template v-slot:default>
        <tbody>

          <!-- Intensity -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-emoticon-dead-outline</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.columnNames.intensity')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.columnNames.intensity')) }}
                  <v-tooltip
                    right
                    v-if="!isPrescriptionIntensityValid || !areHeartRatesValid"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__warning"
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle</v-icon>
                    </template>
                    <p class="mb-0" v-if="!isPrescriptionIntensityValid">{{ $ucfirst($t('expertTool.validationWarning')) }}</p>
                    <p class="mb-0" v-if="!areHeartRatesValid">{{ $ucfirst($t('expertTool.noHeartRateWarning')) }}</p>
                  </v-tooltip>
                </div>
                <div class="recommendation-form__intensities" :class="{'flex-nowrap': !hasHIT}">
                  <recommendation-parameter-interface
                    v-model="intensity"
                    class="recommendation-form__intensity-interface"
                    :valueOptions="intensityOptions"
                    :flagOptions="intensityFlagOptions"
                    :isValueInputDisabled="hasHIT"
                  ></recommendation-parameter-interface>
                  <div
                    class="d-flex recommendation-form__heart-rates"
                    :style="{'width': !hasHIT ? 'auto' : ''}"
                  >
                    <v-text-field
                      v-model="heartRate"
                      :label="`${$ucfirst($t('heartRate'))}${hasHIT ? '*' : ''}`"
                      class="recommendation-form__heart-rate-input"
                      outlined
                      dense
                      type="number"
                      :min="0"
                      @keypress="onKeyPressNumbers"
                      @paste="onPasteNumbers"
                    ></v-text-field>
                    <v-text-field
                      v-if="hasHIT"
                      v-model="heartRateCooldown"
                      :label="`${$ucfirst($t('coolingHeartRate'))}${hasHIT ? '*' : ''}`"
                      class="recommendation-form__heart-rate-input"
                      outlined
                      dense
                      type="number"
                      :min="0"
                      @keypress="onKeyPressNumbers"
                      @paste="onPasteNumbers"
                    ></v-text-field>
                  </div>
                </div>
                <v-checkbox
                  :label="$ucfirst($t('expertTool.includeHIT'))"
                  v-model="hasHIT"
                  class="recommendation-form__checkbox"
                  hide-details
                ></v-checkbox>
              </div>
            </td>
          </tr>

          <!-- Frequency -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-chart-line</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.columnNames.frequency')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.columnNames.frequency')) }}
                  <v-tooltip
                    right
                    v-if="!isPrescriptionFrequencyValid"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__warning"
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.validationWarning')) }}</span>
                  </v-tooltip>
                </div>
                <recommendation-parameter-interface
                  class="recommendation-form__interface"
                  v-model="frequency"
                  :valueOptions="frequencyOptions"
                  :flagOptions="frequencyFlagOptions"
                ></recommendation-parameter-interface>
              </div>
            </td>
          </tr>

          <!-- Session Duration -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-timer-outline</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.columnNames.sessionDuration')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.columnNames.sessionDuration')) }}
                  <v-tooltip
                    right
                    v-if="!isPrescriptionSessionDurationValid"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__warning"
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.validationWarning')) }}</span>
                  </v-tooltip>
                </div>
                <recommendation-parameter-interface
                  class="recommendation-form__interface"
                  v-model="sessionDuration"
                  :flagOptions="sessionDurationFlagOptions"
                  is-numeric-input
                ></recommendation-parameter-interface>
              </div>
            </td>
          </tr>

          <!-- Program Duration -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-calendar-month-outline</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.columnNames.programDuration')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.columnNames.programDuration')) }}
                  <v-tooltip
                    right
                    v-if="!isPrescriptionProgramDurationValid"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__warning"
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.validationWarning')) }}</span>
                  </v-tooltip>
                </div>
                <recommendation-parameter-interface
                  class="recommendation-form__interface"
                  v-model="programDuration"
                  :flagOptions="programDurationFlagOptions"
                  is-numeric-input
                ></recommendation-parameter-interface>
              </div>
            </td>
          </tr>

          <!-- Strength Training -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-dumbbell</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('pa.strengthTraining')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('pa.strengthTraining')) }}
                  <v-tooltip
                    right
                    v-if="!isPrescriptionStrengthTrainingValid"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__warning"
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-alert-circle</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.validationStrengthTrainingWarning')) }}</span>
                  </v-tooltip>
                </div>
                <div class="d-flex">
                  <v-select
                    :items="strengthTrainingOptionsMain"
                    :label="$ucfirst($t('pa.strengthTraining')) + '*'"
                    v-model="strengthTraining"
                    class="recommendation-form__strength-training-input mr-4"
                    outlined
                    dense
                  ></v-select>
                  <v-select
                    :items="strengthTrainingOptionsFlags"
                    v-model="strengthTrainingDependencies"
                    class="mr-4"
                    outlined
                    dense
                    :label="$ucfirst($t('dependencies'))"
                    multiple
                  ></v-select>
                </div>
              </div>
            </td>
          </tr>

          <!-- Additional Training Strategies -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-run</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.columnNames.additionalTrainingStrategies')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.columnNames.additionalTrainingStrategies')) }}
                </div>
                <v-textarea
                  v-model='additionalTrainingStrategies'
                  class="recommendation-form__textarea"
                  outlined
                  dense
                ></v-textarea>
              </div>
            </td>
          </tr>

          <!-- Change Reason -->

          <tr>
            <td>
              <div class="recommendation-form__row">
                <div class="recommendation-form__row-header">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="recommendation-form__icon"
                        v-bind="attrs"
                        v-on="on"
                      >mdi-help-circle-outline</v-icon>
                    </template>
                    <span>{{ $ucfirst($t('expertTool.changeReason')) }}</span>
                  </v-tooltip>
                  {{ $ucfirst($t('expertTool.changeReason')) }}
                </div>
                <v-textarea
                  v-model='changeReason'
                  class="recommendation-form__textarea"
                  hide-details
                  outlined
                  dense
                ></v-textarea>
              </div>
            </td>
          </tr>


          <!-- Start Next Week: can perhaps be shown after release3 -->
          <tr>
            <td>
              <div class="recommendation-form__row">
                <v-checkbox
                    v-model="startRecommendationImmediately"
                    class="recommendation-form__checkbox inline-block"
                    hide-details
                ></v-checkbox>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <label
                        v-bind="attrs"
                        v-on="on"
                    >{{ $ucfirst($t('expertTool.applyPrescriptionImmediately')) }}</label>
                  </template>
                  <span>{{ $ucfirst($t('expertTool.applyPrescriptionImmediately')) }}</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex';
import RecommendationParameterInterface from './RecommendationParameterInterface';

/**
 * Component RecommendationForm.vue
 *
 * @module RecommendationForm
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/SportsGoalBoxes/RecommendationForm.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const commonFlags = [
  'INDIVIDUALIZED',
  'DEPENDENT_ON_DISEASE',
  'DEPENDENT_ON_HOSPITALIZATION_DURATION',
  'DEPENDENT_ON_DEVICE_SETTINGS',
  'DEPENDENT_ON_TYPE_OF_DEVICE',
  'DEPENDENT_ON_FLOW_RATES',
  'DEPENDENT_ON_TYPE_OF_CONDITION',
  'ON_PAIN_TRESHOLD',
  'UP_TO_MYOCARDIAL_ISCHEMIC_THRESHOLD',
  'UNKNOWN'
];

const strengthTrainingFlags = ['YES', 'NO', 'UNKNOWN'];

export default {
  name: 'RecommendationForm',
  components: { RecommendationParameterInterface },
  data() {
    return {
      disabledItemList: [],
    }
  },
  computed: {
    ...mapGetters({
      recommendation: 'expertTool/recommendation',
      hasRecommendation: 'expertTool/hasRecommendation',
      isPrescriptionIntensityValid: 'expertTool/isPrescriptionIntensityValid',
      isPrescriptionFrequencyValid: 'expertTool/isPrescriptionFrequencyValid',
      isPrescriptionSessionDurationValid: 'expertTool/isPrescriptionSessionDurationValid',
      isPrescriptionProgramDurationValid: 'expertTool/isPrescriptionProgramDurationValid',
      isPrescriptionStrengthTrainingValid: 'expertTool/isPrescriptionStrengthTrainingValid',
      selectedRules: 'expertTool/selectedRules'
    }),
    formatOptions() {
      return [
        {
          text: this.$ucfirst(this.$t('general.value', {catalog: 'app_mobile'})),
          value: 'FORMAT_VALUE',
        },
        {
          text: this.$ucfirst(this.$t('range')),
          value: 'FORMAT_RANGE',
        },
      ];
    },
    intensityOptions() {
      return [
        {
          text: this.$ucfirst(this.$t('expertTool.intensity.L.long')),
          value: 'L'
        },
        {
          text: this.$ucfirst(this.$t('expertTool.intensity.M.long')),
          value: 'M'
        },
        {
          text: this.$ucfirst(this.$t('expertTool.intensity.H.long')),
          value: 'H'
        },
      ];
    },
    intensityFlagOptions() {
      const options = commonFlags.map((value) => ({
        text: this.getFlagName(value),
        value,
        disabled: false
      }));

      // add HR_ZONE_NOT_TO_BE_DETERMINED dependency only when ANOMALY__ATRIAL_FIBRILATION_PROVOKED is selected
      if (this.selectedRules.includes('ANOMALY__ATRIAL_FIBRILATION_PROVOKED')) {
        options.push({
          text: this.$t('expertTool.heartRate.notDetermined'),
          value: 'HR_ZONE_NOT_TO_BE_DETERMINED',
          disabled: false
        })
      }

      return this.disableOptions(options, 'intensity');
    },
    startRecommendationImmediately : {
      get() {
        return !this.$store.getters['expertTool/startRecommendationNextWeek'];
      },
      set(startRecommendationImmediately) {
        this.$store.commit('expertTool/setStartRecommendationNextWeek', !startRecommendationImmediately)
      }
    },
    intensity: {
      get() {
        return this.recommendation?.intensities && this.recommendation.intensities.length
          ? this.recommendation.intensities[0]
          : { min: '', max: '', flags: [] };
      },
      set(intensity) {
        if (!this.hasRecommendation) return;

        if (!this.recommendation.intensities.length) {
          this.$store.dispatch('expertTool/addRecommendationIntensity');
        }
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);

        this.$store.dispatch('expertTool/updateRecommendationIntensity', {
          index: 0,
          intensity,
        });
      },
    },
    heartRate: {
      get() {
        return this.recommendation.heartRate;
      },
      set(heartRate) {
        this.$store.dispatch('expertTool/setRecommendationHeartRate', heartRate);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    heartRateCooldown: {
      get() {
        return this.recommendation.heartRateCooldown;
      },
      set(heartRateCooldown) {
        this.$store.dispatch('expertTool/setRecommendationHeartRateCooldown', heartRateCooldown);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    frequency: {
      get() {
        return {
          min: this.recommendation.minFrequency,
          max: this.recommendation.maxFrequency,
          flags: this.recommendation.frequencyFlags,
        };
      },
      set({ min, max, flags }) {
        this.$store.dispatch('expertTool/setRecommendationMinFrequency', min);
        this.$store.dispatch('expertTool/setRecommendationMaxFrequency', max);
        this.$store.dispatch('expertTool/setRecommendationFrequencyFlags', flags);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    frequencyOptions() {
      return [1, 2, 3, 4, 5, 6, 7].map((value) => ({
        text: value,
        value,
      }));
    },
    frequencyFlagOptions() {
      const options = commonFlags.map((value) => ({
        text: this.getFlagName(value),
        value,
        disabled: false
      }));

      return this.disableOptions(options, 'frequency');
    },
    sessionDuration: {
      get() {
        return {
          min: this.recommendation.minSessionDuration,
          max: this.recommendation.maxSessionDuration,
          flags: this.recommendation.sessionDurationFlags,
        };
      },
      set({ min, max, flags }) {
        this.$store.dispatch('expertTool/setRecommendationMinSessionDuration', min);
        this.$store.dispatch('expertTool/setRecommendationMaxSessionDuration', max);
        this.$store.dispatch('expertTool/setRecommendationSessionDurationFlags', flags);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    sessionDurationFlagOptions() {
      const options = commonFlags.map((value) => ({
        text: this.getFlagName(value),
        value,
        disabled: false
      }));

      return this.disableOptions(options, 'sessionDuration');
    },
    programDuration: {
      get() {
        return {
          min: this.recommendation.minProgramDuration,
          max: this.recommendation.maxProgramDuration,
          flags: this.recommendation.programDurationFlags,
        };
      },
      set({ min, max, flags }) {
        this.$store.dispatch('expertTool/setRecommendationMinProgramDuration', min);
        this.$store.dispatch('expertTool/setRecommendationMaxProgramDuration', max);
        this.$store.dispatch('expertTool/setRecommendationProgramDurationFlags', flags);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    programDurationFlagOptions() {
      const options = commonFlags.map((value) => ({
        text: this.getFlagName(value),
        value,
        disabled: false
      }));

      return this.disableOptions(options, 'programDuration');
    },
    strengthTraining: {
      get() {
        const flags = this.selectedStrengthTrainingYesNoFlags;
        return flags && flags.length ? flags[0] : '';
      },
      set(strengthTrainingFlag) {
        const flags = this.selectedStrengthTrainingDependencyFlags;
        this.$store.dispatch(
          'expertTool/setRecommendationStrengthTrainingFlags',
          [strengthTrainingFlag, ...flags]
        );
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    strengthTrainingDependencies: {
      get() {
        return this.selectedStrengthTrainingDependencyFlags;
      },
      set(strengthTrainingFlags) {
        const flags = this.selectedStrengthTrainingYesNoFlags;
        this.$store.dispatch(
          'expertTool/setRecommendationStrengthTrainingFlags',
          [...flags, ...strengthTrainingFlags]
        );
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    selectedStrengthTrainingYesNoFlags() {
      const strengthTrainingFlags = this.recommendation?.strengthTrainingFlags || [];
      return strengthTrainingFlags.filter(i => i === 'YES' || i === 'NO');
    },
    selectedStrengthTrainingDependencyFlags() {
      const strengthTrainingFlags = this.recommendation?.strengthTrainingFlags || [];
      return strengthTrainingFlags.filter(i => i !== 'YES' && i !== 'NO');
    },
    strengthTrainingForWatchers() {
      // convert to the same format as others
      return { flags: this.selectedStrengthTrainingDependencyFlags }
    },
    strengthTrainingOptionsMain() {
      return [
        {
          text: '',
          value: strengthTrainingFlags[2],
          disabled: true
        },
        {
          text: this.getFlagName(strengthTrainingFlags[0]),
          value: strengthTrainingFlags[0],
        },
        {
          text: this.getFlagName(strengthTrainingFlags[1]),
          value: strengthTrainingFlags[1],
        },
      ]
    },
    strengthTrainingOptionsFlags() {
      const options = commonFlags.map((value) => ({
        text: this.getFlagName(value),
        value,
        disabled: false
      }))

      return this.disableOptions(options, 'strengthTrainingForWatchers');
    },
    additionalTrainingStrategies: {
      get() {
        return this.recommendation.additionalTrainingStrategies
      },
      set(text) {
        this.$store.dispatch('expertTool/setAdditionalTrainingStrategies', text);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    changeReason: {
      get() {
        return this.recommendation.changeReason || '';
      },
      set(text) {
        this.$store.dispatch('expertTool/setRecommendationChangeReason', text);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
      },
    },
    hasHIT: {
      get() {
        return this.recommendation.hasHIT;
      },
      set(hasHIT) {
        this.$store.dispatch('expertTool/toggleRecommendationHITIntensity', hasHIT);
        this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);

        if (hasHIT) {
          this.$store.dispatch('expertTool/updateRecommendationIntensity', {
            index: 0,
            intensity: {
              flags: this.intensity.flags,
              min: 'L',
              max: 'H',
            },
          });
        }
      },
    },
    isSaved() {
      return this.$store.getters['expertTool/isRecommendationSaved'];
    },
    areHeartRatesValid() {
      if (this.hasHIT) {
        return !!+this.heartRate && !!+this.heartRateCooldown
      }
      return true
    }
  },
  methods: {
    addIntensity() {
      this.$store.dispatch('expertTool/addRecommendationIntensity');
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    removeIntensity(index) {
      this.$store.dispatch('expertTool/removeRecommendationIntensity', index);
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    updateIntensity(intensity, index) {
      this.$store.dispatch('expertTool/updateRecommendationIntensity', {
        index,
        intensity,
      });
      this.$store.dispatch('patientSummary/toggleUnsavedChanges', true);
    },
    getFlagName(value) {
      switch(value) {
        case 'YES':
          return this.$ucfirst(this.$t('buttons.yes', {catalog: "app_mobile"}));
        case 'NO':
          return this.$ucfirst(this.$t('buttons.no', {catalog: "app_mobile"}));
        case 'UNKNOWN':
          return this.$ucfirst(this.$t('dss.allergies.unknown', {catalog: "app_dashboard"}));
        default:
          return this.$ucfirst(this.$t(`expertTool.flags.${value}`));
      }
    },
    setWatchers() {
      const keys = [
        'intensity',
        'frequency',
        'sessionDuration',
        'programDuration',
        'strengthTrainingForWatchers'
      ]
      keys.forEach(key => {
        this.$watch(key, {
          handler: (current) => {
            if (!current.flags) {
              return;
            }
            // unselect selected flags
            if (current.flags.length > 1 && current.flags.includes('UNKNOWN')) {
              this.unselectFlags(key)
            }
            // add key to the list to disable selection
            if (current.flags.includes('UNKNOWN') && !this.disabledItemList.includes(key)) {
              this.disabledItemList = [...this.disabledItemList, key]
            }
            // remove key from the list to enable selection
            if (!current.flags.length && this.disabledItemList.includes(key)) {
              const list = [...this.disabledItemList]
              list.splice(list.indexOf(key), 1);
              this.disabledItemList = list
            }
          }
        })
      })
    },
    unselectFlags(key) {
      const flag = ['UNKNOWN']
      const intensity = {...this.recommendation.intensities[0], flags: flag}
      const strengthTrainingYesNo = this.selectedStrengthTrainingYesNoFlags;

      switch(key) {
        case 'intensity':
          this.$store.dispatch('expertTool/updateRecommendationIntensity', {index: 0, intensity});
          break;
        case 'frequency':
          this.$store.dispatch('expertTool/setRecommendationFrequencyFlags', flag);
          break;
        case 'sessionDuration':
          this.$store.dispatch('expertTool/setRecommendationSessionDurationFlags', flag);
          break;
        case 'programDuration':
          this.$store.dispatch('expertTool/setRecommendationProgramDurationFlags', flag);
          break;
        case 'strengthTrainingForWatchers':
          this.$store.dispatch(
            'expertTool/setRecommendationStrengthTrainingFlags',
            [...strengthTrainingYesNo, ...flag]
          );
          break;
      }
    },
    disableOptions(options, key) {
      if (this.disabledItemList.includes(key)) {
        return options.map((item) => {
          if (item.value !== 'UNKNOWN') {
            item.disabled = true
          }
          return item
        })
      }
      return options;
    },
    onKeyPressNumbers(event) {
      const onlyNumbers = /\d/
      if (!onlyNumbers.test(event.key)) {
        event.preventDefault();
      }
    },
    onPasteNumbers(event) {
      const onlyNumbers = /^\d+$/
      const pasted = event.clipboardData.getData('text/plain')
      if (!onlyNumbers.test(pasted)) {
        event.preventDefault();
      }
    }
  },
  created() {
    if (this.hasHIT) {
      this.$store.dispatch('expertTool/updateRecommendationIntensity', {
        index: 0,
        intensity: {
          flags: this.intensity.flags,
          min: 'L',
          max: 'H',
        },
      });
    }
  },
  mounted() {
    this.setWatchers();
  }
};
</script>

<style lang="scss">
.recommendation-form {
  margin-top: 24px;
  margin-bottom: 16px;
}

.recommendation-form__row {
  padding-top: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.recommendation-form__icon {
  margin-right: 24px;
}

.recommendation-form__intensities {
  flex-grow: 1;
  display: flex;
}

.recommendation-form__btn {
  position: relative;
  bottom: 2px;
  font-size: 1rem !important;
}

.recommendation-form__heart-rate-input {
  max-width: 216px;
  width: 100%;
  margin-right: 1rem !important;
}

.recommendation-form__interface {
  flex-grow: 1;
}

.recommendation-form__intensity-interface {
  flex-grow: 1;
}

.recommendation-form__strength-training-input {
  max-width: 382px;
}

.recommendation-form__table {
  border-top: thin solid rgba(0, 0, 0, 0.12);

  table > tbody > tr:hover {
    background-color: transparent !important;
  }

  td {
    font-size: 1rem !important;
    font-weight: 600;
  }

  .theme--light.v-input input, .theme--light.v-input textarea {
    color: rgba(0, 0, 0, 0.87);
  }
}

.recommendation-form__row-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.recommendation-form__checkbox.v-input--selection-controls {
  padding-top: 0;
  margin-top: 0;
  margin-bottom: 24px;
}

.recommendation-form__warning {
  margin-left: 16px;
}

@media only screen and (max-width: 1400px) {
  .recommendation-form__intensities {
    flex-wrap: wrap;
  }

  .recommendation-form__heart-rates {
    width: 100%
  }
}
</style>
